/**
 * The EPUB export on the main thread (decisions 7, 14; part 20, table K): the book read as it is now (part 21's table N,
 * the open chapter as typed), an EPUB worker started on it, its progress shown, and the finished file offered to save.
 * The worker does the work, so editing goes on while a book exports (decision 14). Cancelling ends the worker, which
 * keeps nothing; the file offered is let go once the writer has saved it or turned the offer down.
 *
 * The book's reading, the worker, the clock and the browser's object URLs are injected, so the logic runs in tests.
 */
import type { PdfBook } from '@cockatrice/layout';
import { createSignal } from 'solid-js';
import type { Accessor } from 'solid-js';
import { fileName } from '../export/controller.ts';
import { readReply } from './protocol.ts';
import type { EpubJob } from './protocol.ts';

/** What the export shows (K-rows' outcomes). */
export type EpubState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'reading' }
  | { readonly kind: 'exporting'; readonly done: number; readonly total: number }
  | { readonly kind: 'ready'; readonly url: string; readonly name: string; readonly size: number; readonly missing: number }
  | { readonly kind: 'failed'; readonly reason: 'unreadable' | 'failed' };

/** An EPUB worker, as far as the controller drives it (a `Worker`). */
export interface EpubWorker {
  addEventListener(type: 'message' | 'messageerror' | 'error', listener: (event: unknown) => void): void;
  postMessage(message: EpubJob): void;
  terminate(): void;
}

/** What the controller needs; everything with an effect is injected. */
export interface EpubOptions {
  /** The open book as the export takes it (part 21's table N), or null when it can't be read whole. */
  readonly readBook: () => Promise<PdfBook | null>;
  /** Starts an EPUB worker. */
  readonly worker: () => EpubWorker;
  /** The time, in milliseconds since the epoch: the EPUB's `dcterms:modified` (O5). */
  readonly now: () => number;
  /** An object URL for the file (`URL.createObjectURL`), and what lets it go. */
  readonly objectUrl: (file: Blob) => string;
  readonly revokeUrl: (url: string) => void;
}

/** A message event's data; undefined for anything else. */
function dataOf(event: unknown): unknown {
  return typeof event === 'object' && event !== null ? Reflect.get(event, 'data') : undefined;
}

/** The EPUB export's state and its one worker. */
export class EpubController {
  readonly state: Accessor<EpubState>;
  readonly #setState: (state: EpubState) => void;
  readonly #options: EpubOptions;
  /** The export under way: a new number for each, so what an earlier one sends is ignored. */
  #job = 0;
  #worker: EpubWorker | null = null;

  constructor(options: EpubOptions) {
    this.#options = options;
    const [state, setState] = createSignal<EpubState>({ kind: 'idle' });
    this.state = state;
    this.#setState = setState;
  }

  /** K1, K2: exports the book as it is now; nothing while one is under way. A file offered before is let go. */
  start(): void {
    const kind = this.state().kind;
    if (kind === 'reading' || kind === 'exporting') return;
    this.#forget();
    this.#job += 1;
    const job = this.#job;
    this.#setState({ kind: 'reading' });
    this.#options.readBook().then(
      (book) => {
        if (job !== this.#job) return;
        // K7: a chapter that can't be read is never left out of the book.
        if (book === null) this.#setState({ kind: 'failed', reason: 'unreadable' });
        else this.#run(job, book);
      },
      () => {
        // K11.
        this.#end(job, { kind: 'failed', reason: 'failed' });
      },
    );
  }

  /** K4–K6: stops the export under way, keeping nothing of it; a file offered stays offered. */
  cancel(): void {
    const kind = this.state().kind;
    if (kind !== 'reading' && kind !== 'exporting') return;
    this.#job += 1;
    this.#end(this.#job, { kind: 'idle' });
  }

  /** K9: puts the offer or the failure away. */
  dismiss(): void {
    const kind = this.state().kind;
    if (kind !== 'ready' && kind !== 'failed') return;
    this.#forget();
    this.#setState({ kind: 'idle' });
  }

  /** K10: the app closing: nothing under way, nothing kept. */
  stop(): void {
    this.cancel();
    this.dismiss();
  }

  /** K1, K3, K8, K12, K13: starts the worker on the book and follows what it sends while it is the export under way. */
  #run(job: number, book: PdfBook): void {
    let worker: EpubWorker;
    try {
      worker = this.#options.worker();
    } catch {
      this.#end(job, { kind: 'failed', reason: 'failed' });
      return;
    }
    this.#worker = worker;
    const fail = (): void => {
      this.#end(job, { kind: 'failed', reason: 'failed' });
    };
    worker.addEventListener('error', fail);
    worker.addEventListener('messageerror', fail);
    worker.addEventListener('message', (event) => {
      if (job !== this.#job) return;
      const reply = readReply(dataOf(event));
      if (reply === null || reply.kind === 'failed') fail();
      else if (reply.kind === 'progress') this.#setState({ kind: 'exporting', done: reply.done, total: reply.total });
      else {
        const { file, missing } = reply;
        this.#end(job, { kind: 'ready', url: this.#options.objectUrl(file), name: fileName(book.metadata.title, 'epub'), size: file.size, missing });
      }
    });
    this.#setState({ kind: 'exporting', done: 0, total: book.chapters.reduce((sum, chapter) => sum + chapter.text.length, 0) });
    worker.postMessage({ kind: 'epub', book, modified: this.#options.now() });
  }

  /** K4, K5, K8: ends the export `job` with `state`, the worker with it; nothing for a job no longer under way. */
  #end(job: number, state: EpubState): void {
    if (job !== this.#job) return;
    this.#worker?.terminate();
    this.#worker = null;
    this.#setState(state);
  }

  /** K1, K9, K10: lets go of a file offered. */
  #forget(): void {
    const state = this.state();
    if (state.kind === 'ready') this.#options.revokeUrl(state.url);
  }
}
